import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/use-organization';
import { analyzeChurnRisk, ChurnRiskScore, ClientEngagementData } from '@/lib/churn-risk';
import { useBillingModel } from '@/hooks/use-billing-model';
import { ComplianceTrend } from '@/hooks/use-compliance-score';

export interface ClientChurnRisk {
  clientId: string;
  clientName: string;
  riskScore: ChurnRiskScore;
}

export function useChurnRisk(clientId?: string) {
  const [riskScore, setRiskScore] = useState<ChurnRiskScore | null>(null);
  const [loading, setLoading] = useState(true);
  const { model: billingModel, loading: billingLoading } = useBillingModel();

  const loadRisk = useCallback(async () => {
    if (!clientId || billingLoading) {
      if (!clientId) setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(now.getDate() - 60);
      const eightWeeksAgo = new Date();
      eightWeeksAgo.setDate(now.getDate() - 56);

      // Fetch ALL data in parallel for better performance
      const [
        sessionsResult,
        packagesResult,
        clientResult,
        streakResult,
        workoutLogsResult,
        currentComplianceSessionsResult,
        prevComplianceSessionsResult,
        currentNutritionResult,
        prevNutritionResult,
        measurementsResult,
      ] = await Promise.all([
        // Sessions (limited to last 100)
        supabase
          .from('sessions')
          .select('date, cancelled, completed_at')
          .eq('client_id', clientId)
          .order('date', { ascending: false })
          .limit(100),
        // Active package
        supabase
          .from('packages')
          .select('size, sessions_completed, activated_at')
          .eq('client_id', clientId)
          .eq('status', 'active')
          .limit(1),
        // Client for next package date
        supabase
          .from('clients')
          .select('next_package_date')
          .eq('id', clientId)
          .single(),
        // Streak
        supabase
          .from('client_streaks')
          .select('current_streak, longest_streak, last_workout_date, total_workouts')
          .eq('client_id', clientId)
          .single(),
        // Workout logs for plateau detection (last 8 weeks)
        supabase
          .from('workout_logs')
          .select('id, started_at')
          .eq('client_id', clientId)
          .gte('started_at', eightWeeksAgo.toISOString())
          .order('started_at', { ascending: true }),
        // Current period sessions for compliance (last 30 days)
        supabase
          .from('sessions')
          .select('id, cancelled, completed_at')
          .eq('client_id', clientId)
          .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
          .lte('date', now.toISOString().split('T')[0]),
        // Previous period sessions for compliance trend (30-60 days ago)
        supabase
          .from('sessions')
          .select('id, cancelled, completed_at')
          .eq('client_id', clientId)
          .gte('date', sixtyDaysAgo.toISOString().split('T')[0])
          .lt('date', thirtyDaysAgo.toISOString().split('T')[0]),
        // Current period nutrition (last 30 days)
        supabase
          .from('nutrition_daily_summaries')
          .select('summary_date, meal_count')
          .eq('client_id', clientId)
          .gte('summary_date', thirtyDaysAgo.toISOString().split('T')[0])
          .lte('summary_date', now.toISOString().split('T')[0]),
        // Previous period nutrition (30-60 days ago)
        supabase
          .from('nutrition_daily_summaries')
          .select('summary_date, meal_count')
          .eq('client_id', clientId)
          .gte('summary_date', sixtyDaysAgo.toISOString().split('T')[0])
          .lt('summary_date', thirtyDaysAgo.toISOString().split('T')[0]),
        // Recent measurements (last 30 days)
        supabase
          .from('client_measurements')
          .select('id')
          .eq('client_id', clientId)
          .gte('measured_at', thirtyDaysAgo.toISOString()),
      ]);

      // Calculate plateau data from workout logs
      let plateauCount = 0;
      let maxWeeksSinceProgress = 0;
      let hasPlateaus = false;

      const workoutLogs = workoutLogsResult.data;
      if (workoutLogs && workoutLogs.length > 0) {
        const workoutLogIds = workoutLogs.map(wl => wl.id);
        const { data: setLogs } = await supabase
          .from('set_logs')
          .select('weight, exercise_id, workout_log_id')
          .in('workout_log_id', workoutLogIds)
          .not('weight', 'is', null);

        if (setLogs && setLogs.length > 0) {
          const workoutDateMap = new Map(
            workoutLogs.map(wl => [wl.id, wl.started_at])
          );

          // Group by exercise → week → max weight
          const exerciseWeekly = new Map<string, Map<string, number>>();
          for (const set of setLogs) {
            if (!set.weight || !set.exercise_id) continue;
            const workoutDate = workoutDateMap.get(set.workout_log_id);
            if (!workoutDate) continue;

            const d = new Date(workoutDate);
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + 4 - (d.getDay() || 7));
            const yearStart = new Date(d.getFullYear(), 0, 1);
            const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
            const weekKey = `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;

            if (!exerciseWeekly.has(set.exercise_id)) {
              exerciseWeekly.set(set.exercise_id, new Map());
            }
            const weeks = exerciseWeekly.get(set.exercise_id)!;
            const current = weeks.get(weekKey) || 0;
            if (set.weight > current) weeks.set(weekKey, set.weight);
          }

          // Detect plateaus per exercise
          for (const [, weeklyData] of exerciseWeekly) {
            if (weeklyData.size < 4) continue;
            const sorted = Array.from(weeklyData.entries())
              .sort((a, b) => a[0].localeCompare(b[0]));

            let lastProgressIdx = -1;
            let prevMax = 0;
            for (let i = 0; i < sorted.length; i++) {
              if (sorted[i][1] > prevMax) {
                lastProgressIdx = i;
                prevMax = sorted[i][1];
              }
            }
            const weeksSince = sorted.length - 1 - lastProgressIdx;
            if (weeksSince >= 3) {
              plateauCount++;
              hasPlateaus = true;
              if (weeksSince > maxWeeksSinceProgress) {
                maxWeeksSinceProgress = weeksSince;
              }
            }
          }
        }
      }

      // Calculate compliance trend
      const currentSessions = currentComplianceSessionsResult.data || [];
      const prevSessions = prevComplianceSessionsResult.data || [];
      const currentScheduled = currentSessions.filter(s => !s.cancelled).length;
      const currentCompleted = currentSessions.filter(s => !s.cancelled && s.completed_at).length;
      const prevScheduled = prevSessions.filter(s => !s.cancelled).length;
      const prevCompleted = prevSessions.filter(s => !s.cancelled && s.completed_at).length;

      const currentWorkoutComp = currentScheduled > 0 ? (currentCompleted / currentScheduled) * 100 : 100;
      const prevWorkoutComp = prevScheduled > 0 ? (prevCompleted / prevScheduled) * 100 : 100;

      const currentNutrition = currentNutritionResult.data || [];
      const prevNutrition = prevNutritionResult.data || [];
      const currentNutDays = currentNutrition.filter(d => d.meal_count && d.meal_count > 0).length;
      const prevNutDays = prevNutrition.filter(d => d.meal_count && d.meal_count > 0).length;
      const currentNutComp = (currentNutDays / 30) * 100;
      const prevNutComp = (prevNutDays / 30) * 100;

      const currentOverall = Math.round((currentWorkoutComp * 0.5) + (currentNutComp * 0.5));
      const prevOverall = Math.round((prevWorkoutComp * 0.5) + (prevNutComp * 0.5));

      let complianceTrend: ComplianceTrend | null = null;
      const scoreDiff = currentOverall - prevOverall;
      if (scoreDiff >= 10) complianceTrend = 'improving';
      else if (scoreDiff <= -10) complianceTrend = 'declining';
      else complianceTrend = 'stable';

      const engagementData: ClientEngagementData = {
        clientId,
        sessions: sessionsResult.data || [],
        activePackage: packagesResult.data?.[0] || null,
        nextPackageDate: clientResult.data?.next_package_date || null,
        streak: streakResult.data || null,
        progressData: {
          hasPlateaus,
          plateauCount,
          maxWeeksSinceProgress,
          complianceTrend,
          complianceScore: currentOverall,
          recentMeasurementCount: measurementsResult.data?.length || 0,
        },
      };

      const risk = analyzeChurnRisk(engagementData, billingModel);
      setRiskScore(risk);
    } catch (error) {
      console.error('Error loading churn risk:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, billingModel, billingLoading]);

  useEffect(() => {
    loadRisk();
  }, [loadRisk]);

  return { riskScore, loading: loading || billingLoading, reload: loadRisk };
}

export function useChurnRiskAlerts() {
  const [atRiskClients, setAtRiskClients] = useState<ClientChurnRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const { organization } = useOrganization();
  const { model: billingModel, loading: billingLoading } = useBillingModel();

  const loadAlerts = useCallback(async () => {
    if (billingLoading || !organization?.id) return;

    try {
      // Fetch all active clients first (needed for client IDs)
      const { data: clients } = await supabase
        .from('clients')
        .select('id, name, next_package_date')
        .eq('organization_id', organization.id)
        .eq('status', 'active');

      if (!clients?.length) {
        setAtRiskClients([]);
        setLoading(false);
        return;
      }

      const clientIds = clients.map(c => c.id);
      // Only fetch sessions from last 90 days for churn analysis
      const now = new Date();
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(now.getDate() - 90);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(now.getDate() - 60);
      const eightWeeksAgo = new Date();
      eightWeeksAgo.setDate(now.getDate() - 56);

      // Fetch sessions, packages, streaks, and progress data IN PARALLEL
      const [
        sessionsResult,
        packagesResult,
        streaksResult,
        workoutLogsResult,
        currentNutritionResult,
        prevNutritionResult,
        measurementsResult,
      ] = await Promise.all([
        supabase
          .from('sessions')
          .select('client_id, date, cancelled, completed_at')
          .in('client_id', clientIds)
          .gte('date', ninetyDaysAgo.toISOString())
          .order('date', { ascending: false }),
        supabase
          .from('packages')
          .select('client_id, size, sessions_completed, activated_at')
          .in('client_id', clientIds)
          .eq('status', 'active'),
        supabase
          .from('client_streaks')
          .select('client_id, current_streak, longest_streak, last_workout_date, total_workouts')
          .in('client_id', clientIds),
        // Workout logs for plateau detection (last 8 weeks)
        supabase
          .from('workout_logs')
          .select('id, client_id, started_at')
          .in('client_id', clientIds)
          .gte('started_at', eightWeeksAgo.toISOString())
          .order('started_at', { ascending: true }),
        // Current nutrition (last 30 days)
        supabase
          .from('nutrition_daily_summaries')
          .select('client_id, summary_date, meal_count')
          .in('client_id', clientIds)
          .gte('summary_date', thirtyDaysAgo.toISOString().split('T')[0])
          .lte('summary_date', now.toISOString().split('T')[0]),
        // Previous nutrition (30-60 days ago)
        supabase
          .from('nutrition_daily_summaries')
          .select('client_id, summary_date, meal_count')
          .in('client_id', clientIds)
          .gte('summary_date', sixtyDaysAgo.toISOString().split('T')[0])
          .lt('summary_date', thirtyDaysAgo.toISOString().split('T')[0]),
        // Recent measurements (last 30 days)
        supabase
          .from('client_measurements')
          .select('id, client_id')
          .in('client_id', clientIds)
          .gte('measured_at', thirtyDaysAgo.toISOString()),
      ]);

      const allSessions = sessionsResult.data;
      const allPackages = packagesResult.data;
      const allStreaks = streaksResult.data;
      const allWorkoutLogs = workoutLogsResult.data || [];
      const allCurrentNutrition = currentNutritionResult.data || [];
      const allPrevNutrition = prevNutritionResult.data || [];
      const allMeasurements = measurementsResult.data || [];

      // Fetch set logs for plateau detection (batch for all workout logs)
      let allSetLogs: { weight: number | null; exercise_id: string; workout_log_id: string }[] = [];
      if (allWorkoutLogs.length > 0) {
        const workoutLogIds = allWorkoutLogs.map(wl => wl.id);
        // Fetch in chunks of 500 to avoid query limits
        for (let i = 0; i < workoutLogIds.length; i += 500) {
          const chunk = workoutLogIds.slice(i, i + 500);
          const { data: setLogs } = await supabase
            .from('set_logs')
            .select('weight, exercise_id, workout_log_id')
            .in('workout_log_id', chunk)
            .not('weight', 'is', null);
          if (setLogs) allSetLogs = allSetLogs.concat(setLogs);
        }
      }

      // Calculate risk for each client
      const risks: ClientChurnRisk[] = [];

      for (const client of clients) {
        const sessions = allSessions?.filter(s => s.client_id === client.id) || [];
        const activePackage = allPackages?.find(p => p.client_id === client.id) || null;
        const streak = allStreaks?.find(s => s.client_id === client.id) || null;

        // Calculate plateau data for this client
        const clientWorkoutLogs = allWorkoutLogs.filter(wl => wl.client_id === client.id);
        let plateauCount = 0;
        let maxWeeksSinceProgress = 0;
        let hasPlateaus = false;

        if (clientWorkoutLogs.length > 0) {
          const clientWlIds = new Set(clientWorkoutLogs.map(wl => wl.id));
          const clientSetLogs = allSetLogs.filter(sl => clientWlIds.has(sl.workout_log_id));
          const workoutDateMap = new Map(clientWorkoutLogs.map(wl => [wl.id, wl.started_at]));

          const exerciseWeekly = new Map<string, Map<string, number>>();
          for (const set of clientSetLogs) {
            if (!set.weight || !set.exercise_id) continue;
            const workoutDate = workoutDateMap.get(set.workout_log_id);
            if (!workoutDate) continue;

            const d = new Date(workoutDate);
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + 4 - (d.getDay() || 7));
            const yearStart = new Date(d.getFullYear(), 0, 1);
            const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
            const weekKey = `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;

            if (!exerciseWeekly.has(set.exercise_id)) exerciseWeekly.set(set.exercise_id, new Map());
            const weeks = exerciseWeekly.get(set.exercise_id)!;
            const current = weeks.get(weekKey) || 0;
            if (set.weight > current) weeks.set(weekKey, set.weight);
          }

          for (const [, weeklyData] of exerciseWeekly) {
            if (weeklyData.size < 4) continue;
            const sorted = Array.from(weeklyData.entries()).sort((a, b) => a[0].localeCompare(b[0]));
            let lastProgressIdx = -1;
            let prevMax = 0;
            for (let i = 0; i < sorted.length; i++) {
              if (sorted[i][1] > prevMax) { lastProgressIdx = i; prevMax = sorted[i][1]; }
            }
            const weeksSince = sorted.length - 1 - lastProgressIdx;
            if (weeksSince >= 3) {
              plateauCount++;
              hasPlateaus = true;
              if (weeksSince > maxWeeksSinceProgress) maxWeeksSinceProgress = weeksSince;
            }
          }
        }

        // Calculate compliance trend for this client
        const clientCurrentSessions = sessions.filter(s => {
          const d = new Date(s.date);
          return d >= thirtyDaysAgo && d <= now;
        });
        const clientPrevSessions = sessions.filter(s => {
          const d = new Date(s.date);
          return d >= sixtyDaysAgo && d < thirtyDaysAgo;
        });
        const curScheduled = clientCurrentSessions.filter(s => !s.cancelled).length;
        const curCompleted = clientCurrentSessions.filter(s => !s.cancelled && s.completed_at).length;
        const prvScheduled = clientPrevSessions.filter(s => !s.cancelled).length;
        const prvCompleted = clientPrevSessions.filter(s => !s.cancelled && s.completed_at).length;

        const curWorkComp = curScheduled > 0 ? (curCompleted / curScheduled) * 100 : 100;
        const prvWorkComp = prvScheduled > 0 ? (prvCompleted / prvScheduled) * 100 : 100;

        const clientCurNut = allCurrentNutrition.filter(n => n.client_id === client.id);
        const clientPrvNut = allPrevNutrition.filter(n => n.client_id === client.id);
        const curNutDays = clientCurNut.filter(d => d.meal_count && d.meal_count > 0).length;
        const prvNutDays = clientPrvNut.filter(d => d.meal_count && d.meal_count > 0).length;
        const curNutComp = (curNutDays / 30) * 100;
        const prvNutComp = (prvNutDays / 30) * 100;

        const curOverall = Math.round((curWorkComp * 0.5) + (curNutComp * 0.5));
        const prvOverall = Math.round((prvWorkComp * 0.5) + (prvNutComp * 0.5));

        let complianceTrend: ComplianceTrend | null = null;
        const diff = curOverall - prvOverall;
        if (diff >= 10) complianceTrend = 'improving';
        else if (diff <= -10) complianceTrend = 'declining';
        else complianceTrend = 'stable';

        const clientMeasurements = allMeasurements.filter(m => m.client_id === client.id);

        const engagementData: ClientEngagementData = {
          clientId: client.id,
          sessions,
          activePackage,
          nextPackageDate: client.next_package_date,
          streak,
          progressData: {
            hasPlateaus,
            plateauCount,
            maxWeeksSinceProgress,
            complianceTrend,
            complianceScore: curOverall,
            recentMeasurementCount: clientMeasurements.length,
          },
        };

        const riskScore = analyzeChurnRisk(engagementData, billingModel);

        // Only include medium risk or higher
        if (riskScore.level !== 'low') {
          risks.push({
            clientId: client.id,
            clientName: client.name,
            riskScore,
          });
        }
      }

      // Sort by risk score descending
      risks.sort((a, b) => b.riskScore.score - a.riskScore.score);

      setAtRiskClients(risks);
    } catch (error) {
      console.error('Error loading churn risk alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [billingModel, billingLoading, organization?.id]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  return { atRiskClients, loading: loading || billingLoading, reload: loadAlerts };
}
